// O2D.P-PRD-018 §TR4
// Event-name constants + detail types for the two CustomEvents dispatched by
// `<o2d-rich-text-editor>`. Modelled on the whiteboard blueprint
// (`packages/whiteboard/src/types/whiteboard-changed-event.types.ts:9,25-28,40-43`).

import type { RichTextDocument } from './rich-text-document.type.js';

/**
 * Canonical event name for the debounced changed CustomEvent dispatched by
 * `<o2d-rich-text-editor>`. Prefixed (mirror whiteboard `whiteboard-changed`) to
 * avoid collisions with the very generic `'changed'` identifier used by
 * form-input libraries.
 */
export const RICH_TEXT_CHANGED_EVENT = 'rich-text-changed' as const;

/**
 * Canonical event name for the draft-restored CustomEvent dispatched by
 * `<o2d-rich-text-editor>` when a per-object localStorage draft is restored on
 * load (OQ4). It is the only reliable way for a consumer to display "restored
 * draft" — `isDirty` alone cannot distinguish a restore from a live edit.
 */
export const RICH_TEXT_DRAFT_RESTORED_EVENT = 'rich-text-draft-restored' as const;

/**
 * Canonical event name for the CustomEvent dispatched by `<o2d-rich-text-editor>`
 * when an image is clicked in VIEW mode, or DOUBLE-clicked in EDIT mode
 * (Anke, 2026-09-26). The package only reports the click; showing an enlarged
 * preview is the host's job.
 *
 * The event is cancelable. A host that handles the click calls
 * `preventDefault()` on it; the element then also cancels the original click and
 * stops its propagation, so a clickable ancestor (a task card, a tile) does not
 * act on the same click. Without a host reaction the click stays untouched. In
 * edit mode a single click never dispatches it — there it selects the image for
 * resizing; only a double click does.
 */
export const RICH_TEXT_IMAGE_CLICK_EVENT = 'rich-text-image-click' as const;

/**
 * Detail payload of the `rich-text-changed` CustomEvent.
 *
 * Emitted at most once per 300ms debounce window after an editor update.
 *
 * - `doc`: current document (ProseMirror-JSON) at emit time.
 * - `isDirty`: `true` iff the content signature of `doc` differs from the
 *   signature captured by the last `content` assignment or `markSaved()` call.
 */
export interface RichTextChangedEventDetail {
  readonly doc: RichTextDocument;
  readonly isDirty: boolean;
}

/**
 * Detail payload of the `rich-text-draft-restored` CustomEvent.
 *
 * - `objectId`: the SID (`object-id`) whose localStorage draft was restored.
 */
export interface RichTextDraftRestoredEventDetail {
  readonly objectId: string;
}

/**
 * Detail payload of the `rich-text-image-click` CustomEvent.
 *
 * - `src`: the clicked image's resolved URL (the `img.src` property, not the raw
 *   attribute).
 * - `alt`: its alternative text, `''` when the image has none.
 */
export interface RichTextImageClickEventDetail {
  readonly src: string;
  readonly alt: string;
}

/**
 * Type aliases for the CustomEvents dispatched by `<o2d-rich-text-editor>`.
 * Consumers can use these to declare a typed listener variable when needed.
 */
export type RichTextChangedEvent = CustomEvent<RichTextChangedEventDetail>;
export type RichTextDraftRestoredEvent = CustomEvent<RichTextDraftRestoredEventDetail>;
export type RichTextImageClickEvent = CustomEvent<RichTextImageClickEventDetail>;

// Global augmentation: addEventListener('rich-text-changed' | 'rich-text-draft-restored' |
// 'rich-text-image-click', ...) becomes type-safe for any HTMLElement consumer that imports
// from '@o2d/rich-text-editor'. The image click bubbles up to `document`, where a host
// typically listens once, hence `DocumentEventMap` as well. Compile-time only — no runtime
// effect.
declare global {
  interface HTMLElementEventMap {
    'rich-text-changed': RichTextChangedEvent;
    'rich-text-draft-restored': RichTextDraftRestoredEvent;
    'rich-text-image-click': RichTextImageClickEvent;
  }
  interface DocumentEventMap {
    'rich-text-image-click': RichTextImageClickEvent;
  }
}
